import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API, formatDate } from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Building2, Mail, Phone, Loader2, Trash2, Edit } from "lucide-react";

export default function CustomersPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
  });

  const [editData, setEditData] = useState({
    name: "",
    email: "",
    contact_person: "",
    phone: "",
    address: "",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code) {
      toast.error("Please fill in name and code");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/customers`, formData, {
        withCredentials: true,
      });
      setCustomers((prev) => [response.data, ...prev]);
      toast.success("Customer created successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        code: "",
        contact_person: "",
        email: "",
        phone: "",
        address: "",
      });
    } catch (err) {
      console.error("Failed to create customer:", err);
      toast.error(err.response?.data?.detail || "Failed to create customer");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(search.toLowerCase()) ||
      customer.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleDeleteCustomer = async (customerId, customerName) => {
    setDeleting(customerId);
    try {
      await axios.delete(`${API}/customers/${customerId}`, { withCredentials: true });
      toast.success(`Customer "${customerName}" deleted successfully`);
      setCustomers((prev) => prev.filter((c) => c.customer_id !== customerId));
    } catch (err) {
      console.error("Failed to delete customer:", err);
      toast.error(err.response?.data?.detail || "Failed to delete customer");
    } finally {
      setDeleting(null);
    }
  };

  const handleRequestDeletion = async (customerId, customerName) => {
    setDeleting(customerId);
    try {
      await axios.post(`${API}/deletion-requests`, {
        entity_type: "customer",
        entity_id: customerId,
        entity_name: customerName,
      });
      toast.success("Deletion request submitted for approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit deletion request");
    } finally {
      setDeleting(null);
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setEditData({
      name: customer.name || "",
      email: customer.email || "",
      contact_person: customer.contact_person || "",
      phone: customer.phone || "",
      address: customer.address || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    setSubmitting(true);
    try {
      const res = await axios.put(`${API}/customers/${editingCustomer.customer_id}`, editData);
      setCustomers((prev) =>
        prev.map((c) => (c.customer_id === editingCustomer.customer_id ? res.data : c))
      );
      toast.success("Customer updated successfully");
      setEditDialogOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update customer");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CustomersSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">
            Manage customer accounts
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {isSuperAdmin && (
            <DialogTrigger asChild>
              <Button className="rounded-sm" data-testid="new-customer-button">
                <Plus className="w-4 h-4 mr-2" />
                New Customer
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Customer</DialogTitle>
              <DialogDescription>
                Add a new customer to the system.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Customer Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., TJX Company"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="rounded-sm"
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Customer Code *</Label>
                  <Input
                    id="code"
                    placeholder="e.g., TJX"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                    }
                    className="rounded-sm"
                    data-testid="customer-code-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  placeholder="Contact name"
                  value={formData.contact_person}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, contact_person: e.target.value }))
                  }
                  className="rounded-sm"
                  data-testid="customer-contact-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="rounded-sm"
                    data-testid="customer-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="rounded-sm"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="Full address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, address: e.target.value }))
                  }
                  className="rounded-sm"
                  data-testid="customer-address-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-customer-button">
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Create Customer"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="customer-search"
          />
        </div>
      </div>

      {/* Customers Table */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Code
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Name
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Contact
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Email
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Phone
                </TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-widest">
                  Created
                </TableHead>
                {isAdmin && (
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-right">
                    Actions
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.customer_id} data-testid={`customer-row-${customer.customer_id}`}>
                  <TableCell className="font-mono font-medium">
                    {customer.code}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {customer.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {customer.contact_person || "-"}
                  </TableCell>
                  <TableCell>
                    {customer.email ? (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[150px]">{customer.email}</span>
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {customer.phone ? (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {customer.phone}
                      </div>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(customer.created_at)}
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(customer)}
                          data-testid={`edit-customer-${customer.customer_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {isSuperAdmin ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                data-testid={`delete-customer-${customer.customer_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Customer</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{customer.name}"? This action cannot be undone.
                                  <br /><br />
                                  <strong>Note:</strong> Customers with active orders cannot be deleted.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => handleDeleteCustomer(customer.customer_id, customer.name)}
                                  disabled={deleting === customer.customer_id}
                                >
                                  {deleting === customer.customer_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                  ) : null}
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-amber-600 hover:text-amber-700"
                            onClick={() => handleRequestDeletion(customer.customer_id, customer.name)}
                            disabled={deleting === customer.customer_id}
                            data-testid={`request-delete-customer-${customer.customer_id}`}
                          >
                            {deleting === customer.customer_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredCustomers.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No customers found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Customer</DialogTitle>
            <DialogDescription>
              Update customer details. Code cannot be changed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Customer Name *</Label>
              <Input
                id="edit-name"
                value={editData.name}
                onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))}
                className="rounded-sm"
                data-testid="edit-customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData((prev) => ({ ...prev, email: e.target.value }))}
                className="rounded-sm"
                data-testid="edit-customer-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact">Contact Person</Label>
              <Input
                id="edit-contact"
                value={editData.contact_person}
                onChange={(e) => setEditData((prev) => ({ ...prev, contact_person: e.target.value }))}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editData.phone}
                  onChange={(e) => setEditData((prev) => ({ ...prev, phone: e.target.value }))}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editData.address}
                  onChange={(e) => setEditData((prev) => ({ ...prev, address: e.target.value }))}
                  className="rounded-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-customer-button">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CustomersSkeleton = () => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <Skeleton className="h-10 w-32" />
      <Skeleton className="h-10 w-36" />
    </div>
    <Skeleton className="h-10 w-full max-w-md" />
    <Skeleton className="h-96 w-full" />
  </div>
);
